import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, Shield, Zap, Lock, AlertCircle } from 'lucide-react';

export const FAQView = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      category: 'Minting',
      icon: Zap,
      color: 'bg-blue-600',
      questions: [
        {
          q: 'How do I mint a Dunk Poge NFT?',
          a: 'Connect your wallet, go to the MINT tab, select quantity (1-10), and click MINT NOW. Each NFT costs 0.002 ETH. Your NFT traits are generated on-chain at mint time.'
        },
        {
          q: 'How many can I mint?',
          a: 'Maximum 10 NFTs per wallet during public sale. There is a fixed supply of 10,000 NFTs total.'
        },
        {
          q: 'When does minting start?',
          a: 'Check the sale status on the MINT page. When "SALE ACTIVE" is shown, minting is live.'
        },
        {
          q: 'Can I choose my traits?',
          a: 'No. Traits are randomly generated on-chain using blockchain data (block hash, timestamp, etc.). This ensures fairness and prevents manipulation.'
        }
      ]
    },
    {
      category: 'Staking',
      icon: Lock,
      color: 'bg-green-600',
      questions: [
        {
          q: 'How do I stake my NFTs?',
          a: '1. Go to STAKE tab. 2. Approve the staking contract (one-time). 3. Select NFTs to stake. 4. Click STAKE. Your NFTs are transferred to the staking contract and start earning POGE immediately.'
        },
        {
          q: 'When do I receive rewards?',
          a: 'Rewards accumulate every second based on emission rates. You can claim anytime or they auto-claim when you unstake. No minimum wait time.'
        },
        {
          q: 'Can I lose my NFTs by staking?',
          a: 'No. You can unstake anytime with no penalty. Use emergencyWithdraw() if needed (forfeits rewards but returns NFTs immediately).'
        },
        {
          q: 'What are loyalty multipliers?',
          a: 'Stake longer, earn more. Multipliers grow from 1x to 2x over 180 days. Example: Day 1 = 1x, Day 90 = 1.5x, Day 180+ = 2x max.'
        },
        {
          q: 'Do multipliers reset when I claim?',
          a: 'No! Multipliers are based on stake duration, not claim frequency. Claiming rewards does NOT reset your loyalty progress.'
        }
      ]
    },
    {
      category: 'Rewards & Tokenomics',
      icon: Zap,
      color: 'bg-purple-600',
      questions: [
        {
          q: 'How much POGE do I earn?',
          a: 'Base rate starts at ~10 POGE/day per NFT, decaying to ~1 POGE/day over 730 days. With 2x multiplier, you can earn up to 20 POGE/day initially, tapering to 2 POGE/day.'
        },
        {
          q: 'What is POGE used for?',
          a: 'POGE is the ecosystem token. Future utility TBD, but current use is staking rewards. Fixed supply of 1 billion tokens.'
        },
        {
          q: 'What if the reward pool runs out?',
          a: 'The contract gracefully handles low balances by paying what\'s available. Events are emitted for transparency. 500M POGE allocated to staking provides a 27.6% safety buffer.'
        },
        {
          q: 'Can rewards be increased later?',
          a: 'No. The staking contract is immutable with no admin functions. Emission rates are fixed in code and cannot be changed.'
        }
      ]
    },
    {
      category: 'Technical & Security',
      icon: Shield,
      color: 'bg-red-600',
      questions: [
        {
          q: 'Are the contracts audited?',
          a: 'No. The contracts use battle-tested patterns: ERC721A, ReentrancyGuard, SafeERC20, and Checks-Effects-Interactions. Code is open source on Etherscan and GitHub for community review.'
        },
        {
          q: 'Can the team rug pull?',
          a: 'No. The staking contract has zero admin functions - no pause, no upgrade, no withdrawal. NFT contract owner can only toggle sale and withdraw mint funds. Your NFTs, staked or unstaked, are always yours.'
        },
        {
          q: 'What happens if the website goes down?',
          a: 'Everything works without the website. Interact directly with contracts via Etherscan. NFT metadata is fully on-chain, not on IPFS or servers.'
        },
        {
          q: 'Where is the metadata stored?',
          a: 'Fully on-chain. The SVG and the logic is baked into the contract. No IPFS, no servers, no external dependencies. Your NFT will exist as long as Ethereum exists.'
        },
        {
          q: 'Can NFTs be censored or frozen?',
          a: 'No. Standard ERC721 with no blacklist or burn functionality. Once minted, NFTs are yours to transfer, stake, or sell freely.'
        }
      ]
    },
    {
      category: 'Troubleshooting',
      icon: AlertCircle,
      color: 'bg-yellow-600',
      questions: [
        {
          q: 'Transaction failed - what do I do?',
          a: 'Check: 1) Sufficient ETH for gas, 2) Sale is active (for minting), 3) Approved staking contract (for staking), 4) Not exceeding limits. Try increasing gas limit if needed.'
        },
        {
          q: 'I approved but can\'t stake?',
          a: 'Approval is per-contract. Make sure you approved the staking contract address shown in the STAKE tab, not the NFT contract.'
        },
        {
          q: 'My NFT image isn\'t loading?',
          a: 'NFTs are SVG - some wallets/marketplaces may take time to render. You may view them directly via the contract tokenURI() on Etherscan.'
        },
        {
          q: 'Can I unstake immediately?',
          a: 'Yes, anytime. No lock period, no fees. Just go to STAKE tab, select your NFTs, and click UNSTAKE. Rewards claim automatically.'
        }
      ]
    }
  ];

  return (
    <div className="space-y-12">
      {/* Hero */}
      <div className="text-center">
        <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 border-4 border-black shadow-[8px_8px_0_0_#000] mb-6">
          <h1 className="text-4xl md:text-6xl font-black uppercase font-mono flex items-center gap-4 justify-center">
            <HelpCircle size={48} />
            FAQ
          </h1>
        </div>
        <div className="bg-white text-black p-6 border-4 border-black shadow-[8px_8px_0_0_#000] max-w-2xl mx-auto">
          <p className="text-xl font-black uppercase mb-2 font-mono">
            FREQUENTLY ASKED QUESTIONS
          </p>
          <p className="text-base font-mono">
            Quick answers to common questions about minting, staking, and rewards
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        {[
          { label: 'SUPPLY', value: '10,000' },
          { label: 'PRICE', value: '0.002 ETH' },
          { label: 'MAX MULT', value: '2.0x' },
          { label: 'NO LOCK', value: 'ANYTIME' }
        ].map((stat, i) => (
          <div key={i} className="bg-gradient-to-br from-green-400 to-cyan-400 text-black p-6 border-4 border-black shadow-[6px_6px_0_0_#000] text-center">
            <div className="text-sm font-bold mb-2 opacity-75">{stat.label}</div>
            <div className="text-2xl font-black font-mono">{stat.value}</div>
          </div>
        ))}
      </div>

      {/* FAQ Categories */}
      {faqs.map((category, catIndex) => {
        const Icon = category.icon;
        return (
          <div key={catIndex} className={`${category.color} text-white p-8 border-4 border-white shadow-[8px_8px_0_0_#fff]`}>
            <div className="flex items-center gap-3 mb-6">
              <Icon size={32} />
              <h2 className="text-3xl font-black uppercase font-mono">{category.category}</h2>
            </div>

            <div className="space-y-4">
              {category.questions.map((faq, qIndex) => {
                const globalIndex = `${catIndex}-${qIndex}`;
                const isOpen = openIndex === globalIndex;
                
                return (
                  <div key={qIndex} className="bg-white text-black border-4 border-black">
                    <button
                      onClick={() => toggleQuestion(globalIndex)}
                      className="w-full p-6 flex items-center justify-between gap-4 text-left hover:bg-gray-100 transition-colors"
                    >
                      <span className="font-black text-lg font-mono flex-1">{faq.q}</span>
                      <div className="flex-shrink-0">
                        {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                      </div>
                    </button>
                    
                    {isOpen && (
                      <div className="px-6 pb-6 border-t-4 border-black pt-4">
                        <p className="text-base font-mono leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Still Have Questions */}
      <div className="bg-black text-white p-8 border-4 border-green-400 shadow-[8px_8px_0_0_#00ff00]">
        <h2 className="text-3xl font-black uppercase font-mono mb-6 text-center">
          STILL HAVE QUESTIONS?
        </h2>
        <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
          <a
            href="https://discord.gg/7PsZwC3TZX"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-black font-black py-4 px-6 border-4 border-white hover:bg-gray-200 transition-all text-center no-underline uppercase"
          >
            💬 DISCORD
          </a>
          <a
            href="https://twitter.com/dunkpoge"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-black font-black py-4 px-6 border-4 border-white hover:bg-gray-200 transition-all text-center no-underline uppercase"
          >
            🐦 TWITTER
          </a>
          <a
            href="https://github.com/dunkpoge"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-black font-black py-4 px-6 border-4 border-white hover:bg-gray-200 transition-all text-center no-underline uppercase"
          >
            💻 GITHUB
          </a>
        </div>
        <p className="text-center mt-6 text-sm font-mono opacity-75">
          Join our community for support and updates
        </p>
      </div>

      {/* Bottom Badge */}
      <div className="text-center">
        <div className="inline-block bg-green-400 text-black px-8 py-4 border-4 border-black shadow-[8px_8px_0_0_#000]">
          <p className="text-xl font-black font-mono uppercase">
            Much Help • Very Clear • Wow
          </p>
        </div>
      </div>
    </div>
  );
};